import React, { useState } from 'react';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Icon,
  Button,
  Divider,
  SimpleGrid,
  Input,
  Select,
} from '@chakra-ui/react';
import ZoomableAvatar from 'components/Avatar/ZoomableAvatar.js';
import {
  FiCheckCircle,
  FiXCircle,
  FiInfo,
  FiLogOut,
  FiLogIn,
  FiMapPin,
  FiClock,
  FiSlash,
  FiActivity,
  FiMap,
  FiAlertTriangle,
  FiUsers,
  FiCalendar,
  FiAlertCircle,
  FiWifiOff,
  FiExternalLink,
} from 'react-icons/fi';
import { normalizeCourseName, codeToDisplayLabel } from 'utils/courseUtils';
import StaffPicker from 'components/Search/StaffPicker';
import GuardianPicker from 'components/Search/GuardianPicker';
import ConsultaResult from './ConsultaResult';

const openProfile = (wpUserId) => {
  if (!wpUserId) return;
  window.open(`${window.location.origin}/#/admin/usuario/${wpUserId}`, '_blank');
};

// ── Scan mode ─────────────────────────────────────────────────────────────────

const MODE_CONFIG = {
  access_log:   { icon: FiLogIn,       solid: '#3DB871', confirmLabel: 'Confirmar Entrada', label: 'ENTRADA' },
  exit_log:     { icon: FiLogOut,      solid: '#D97706', confirmLabel: 'Confirmar Salida',  label: 'SALIDA' },
  info_only:    { icon: FiInfo,        solid: '#2A66B4', confirmLabel: null,                 label: 'CONSULTA' },
  event_ticket: { icon: FiCheckCircle, solid: '#6D28D9', confirmLabel: 'Validar Entrada',    label: 'VALIDAR' },
};

// ── Student status ────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  suspended: {
    solid: '#DC2626',
    pastelBg: 'rose.bg', pastelInk: 'rose.ink',
    label: 'SUSPENDIDO',
    icon: FiSlash,
    blocksEntry: true,
    entryMessage: 'El alumno tiene el acceso bloqueado.',
    exitMessage: 'El alumno está suspendido.',
  },
  medical_leave: {
    solid: '#D97706',
    pastelBg: 'butter.bg', pastelInk: 'butter.ink',
    label: 'LICENCIA MÉDICA',
    icon: FiActivity,
    blocksEntry: false,
    entryMessage: 'El alumno está en licencia médica.',
    exitMessage: 'El alumno está en licencia médica.',
  },
  pedagogical_exit: {
    solid: '#6D28D9',
    pastelBg: 'lav.bg', pastelInk: 'lav.ink',
    label: 'SALIDA PEDAGÓGICA',
    icon: FiMap,
    blocksEntry: false,
    entryMessage: 'Regresando de salida pedagógica.',
    exitMessage: 'Salida pedagógica autorizada.',
  },
  sick_exit: {
    solid: '#0F766E',
    pastelBg: 'mint.bg', pastelInk: 'mint.ink',
    label: 'SALIDA POR ENFERMEDAD',
    icon: FiActivity,
    blocksEntry: false,
    entryMessage: 'Regresando de salida por enfermedad.',
    exitMessage: 'Salida por enfermedad autorizada.',
  },
};

// ── Early-exit reasons ────────────────────────────────────────────────────────

// Toda salida anticipada implica apoderado presente y queda registrado en
// "Quién retira" (siempre obligatorio) — el motivo ya no necesita distinguir
// "retiro apoderado" como caso aparte.
const EXIT_REASONS = [
  { key: 'pedagogical', label: 'Salida pedagógica', icon: FiMap      },
  { key: 'pickup',      label: 'Retiro',            icon: FiUsers    },
  { key: 'sick',        label: 'Enfermedad',        icon: FiActivity },
];

// Mismas 3 opciones que Admin > Estados de Alumnos (StudentStatusesTab).
const SICK_EXIT_TITLES = ['Accidente escolar', 'Enfermedad', 'Otros'];

// ── Tamaños responsivos compartidos ─────────────────────────────────────────
// En desktop (md+) todas las proporciones crecen bastante — esta pantalla se
// opera de un vistazo y a distancia, así que texto/click-targets grandes
// importan más que la densidad de información.
const DETAIL_TEXT = { base: '14px', md: '15px' };
const DETAIL_PAD = { base: 3, md: '14px' };
const BTN_LG = { h: { base: '56px', md: '64px' }, fontSize: { base: '16px', md: '18px' }, borderRadius: '14px' };
const BTN_GHOST = { h: { base: '48px', md: '52px' }, fontSize: { base: '15px', md: '16px' } };

// ── Shared: student info row ──────────────────────────────────────────────────

// OJO: Avatar (multi-part) resuelve `size` con un lookup directo de objeto
// (styleConfig.sizes[props.size]) — pasarle un objeto responsivo ahí NO
// resuelve por breakpoint, simplemente no matchea nada y el Avatar queda
// SIN width/height (rompe el layout con la foto real). Por eso `size` va
// fijo y el agrandado en desktop se hace con `boxSize`, que sí es un style
// prop normal y sí soporta objetos responsivos.
const AVATAR_BOX_SIZE = {
  xl: { base: '88px', md: '104px' },
  md: { base: '48px', md: '60px' },
};

const StudentInfo = ({ student, group, size = 'xl', showTime = true, location }) => {
  const fullName = `${student?.first_name ?? ''} ${student?.last_name ?? ''}`.trim();
  const hasProfile = !!student?.wp_user_id;
  const isXl = size === 'xl';
  return (
    <VStack spacing={3} align="stretch">
      <HStack spacing={{ base: 3, md: 4 }} align="center">
        <HStack
          as={hasProfile ? 'button' : 'div'}
          type={hasProfile ? 'button' : undefined}
          role={hasProfile ? 'group' : undefined}
          onClick={hasProfile ? () => openProfile(student.wp_user_id) : undefined}
          spacing={{ base: 3, md: 4 }} align="center" flex={1} minW={0} textAlign="left"
          cursor={hasProfile ? 'pointer' : 'default'}
          title={hasProfile ? 'Abrir ficha del alumno' : undefined}
        >
          <ZoomableAvatar
            src={student?.avatar_url || null}
            name={fullName}
            size={size}
            boxSize={AVATAR_BOX_SIZE[size] ?? undefined}
            borderRadius={isXl ? '14px' : '10px'}
            flexShrink={0}
            bg="coral.soft"
            color="coral.700"
          />
          <VStack align="flex-start" spacing={1} flex={1} minW={0}>
            {/* Nombres arriba, apellidos abajo (en vez de una sola línea) —
                más legible y evita que el nombre completo se vea gigante o
                se corte en el ancho angosto de la columna. */}
            <HStack spacing="6px" minW={0} align="flex-start">
              <VStack align="flex-start" spacing="0" minW={0}>
                <Text fontSize={isXl ? { base: '17px', md: '20px' } : { base: '14px', md: '16px' }}
                  fontWeight="800" color="ink.900" lineHeight="1.3" noOfLines={1}
                  _groupHover={{ textDecoration: hasProfile ? 'underline' : 'none' }}>
                  {student?.first_name || '—'}
                </Text>
                <Text fontSize={isXl ? { base: '17px', md: '20px' } : { base: '14px', md: '16px' }}
                  fontWeight="800" color="ink.900" lineHeight="1.3" noOfLines={1}>
                  {student?.last_name || ''}
                </Text>
              </VStack>
              {hasProfile && <Icon as={FiExternalLink} boxSize={{ base: 3, md: 3.5 }} color="ink.400" flexShrink={0} mt="2px" />}
            </HStack>
            <Text fontSize={{ base: '13px', md: '14.5px' }} color="ink.400" fontWeight="600" noOfLines={1}>
              {student?.username ?? student?.rut}
            </Text>
            {group && (
              <Box as="span" px={{ base: '10px', md: '12px' }} py={{ base: '3px', md: '4px' }} borderRadius="999px"
                bg="coral.soft" color="coral.700"
                fontSize={{ base: '12px', md: '13.5px' }} fontWeight="700" maxW="100%" isTruncated>
                {group}
              </Box>
            )}
          </VStack>
        </HStack>
      </HStack>
      {/* Hora/ubicación siempre debajo del perfil (nunca al costado del
          nombre) — al costado quedaba apretado y competía visualmente con
          el nombre en desktop. */}
      {(showTime || location) && (
        <HStack spacing={3} justify={{ base: 'center', md: 'flex-start' }} flexWrap="wrap">
          {showTime && (
            <HStack spacing={1}>
              <Icon as={FiClock} color="ink.400" boxSize={3.5} />
              <Text fontSize={{ base: '13px', md: '14.5px' }} color="ink.400" fontWeight="600">
                {new Date().toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}
              </Text>
            </HStack>
          )}
          {location && (
            <HStack spacing={1}>
              <Icon as={FiMapPin} color="ink.400" boxSize={3.5} />
              <Text fontSize={{ base: '13px', md: '14.5px' }} color="ink.400" fontWeight="600" noOfLines={1}>{location}</Text>
            </HStack>
          )}
        </HStack>
      )}
    </VStack>
  );
};

// El ícono va en un chip circular tintado (en vez de suelto sobre la tarjeta
// translúcida) — sin esto se pierde visualmente, ya que antes tenía una barra
// de color sólido detrás que le daba peso. `bg` siempre llega como hex plano
// (#RRGGBB) en todos los llamados, así que el sufijo de alpha (`1F`≈12%,
// `66`≈40%) funciona sin tocar ningún call site.
const StatusBanner = ({ bg, icon, label, subtitle }) => (
  <Box px={{ base: 5, md: 8 }} pt={{ base: 4, md: 5 }} pb={{ base: '2px', md: '4px' }}>
    <VStack spacing={{ base: '2px', md: '4px' }}>
      <HStack justify="center" spacing={{ base: 2, md: 3 }}>
        <Flex
          w={{ base: '30px', md: '34px' }} h={{ base: '30px', md: '34px' }}
          borderRadius="full" align="center" justify="center" flexShrink={0}
          bg={`${bg}1F`} border="1.5px solid" borderColor={`${bg}66`}
        >
          <Icon as={icon} color={bg} boxSize={{ base: 4, md: '18px' }} />
        </Flex>
        <Text color={bg} fontWeight="800" fontSize={{ base: '20px', md: '24px' }} letterSpacing="0.04em">{label}</Text>
      </HStack>
      {subtitle && <Text color={bg} opacity={0.85} fontSize={{ base: '12px', md: '13px' }} fontWeight="600">{subtitle}</Text>}
    </VStack>
  </Box>
);

// countdown (opcional): segundos que faltan para que "Auto" confirme esta
// tarjeta solo (ver AUTO_CONFIRM_DELAY_MS en ScannerShell) — se muestra
// justo ARRIBA del botón "Cancelar"/"Cerrar" (no en el banner de arriba),
// para que quede pegado a la acción que efectivamente lo detiene.
const AutoCountdownNote = ({ countdown }) => {
  if (countdown == null) return null;
  return (
    <HStack justify="center" spacing="6px" mb="2px">
      <Icon as={FiClock} boxSize="12px" color="coral.600" />
      <Text fontSize="12.5px" color="coral.600" fontWeight="700">
        Auto confirma en {countdown}s
      </Text>
    </HStack>
  );
};

// ── Layout desktop: identidad a la izquierda, acción a la derecha ─────────────
// En mobile todo se apila en una columna (como antes). En desktop (md+) se
// distribuye horizontalmente: el alumno queda fijo a la izquierda para
// verificarlo de un vistazo, y toda la acción (detalle + botones) ocupa el
// resto del ancho a la derecha. `align="flex-start"` a propósito — NO se
// estiran ambas columnas a la altura de la más alta, cada una ocupa solo lo
// que su contenido necesita, así no quedan espacios vacíos colgando cuando
// una columna es más corta que la otra (evita necesitar scroll de más).
//
// `rightWidth`: cuando la columna derecha solo trae 1-2 botones (Entrada,
// Salida normal, Validación) darle todo el flex sobrante se ve desproporcionado
// — ahí la izquierda pasa a fluida (flex:1) y la derecha se fija a este ancho
// (ej. "30%"). Cuando la derecha trae detalle/formularios (salida anticipada,
// suspendido, licencia médica, salida pedagógica) se deja sin especificar y
// mantiene el patrón por defecto (izquierda fija 280px, derecha flex:1).
const SplitBody = ({ left, right, rightWidth }) => (
  <Flex direction={{ base: 'column', md: 'row' }} align={{ base: 'stretch', md: 'flex-start' }}
    p={{ base: 4, md: 6 }} gap={{ base: 4, md: 6 }}>
    <VStack spacing={3} align="stretch"
      flex={rightWidth ? '1' : { md: '0 0 280px' }}
      w={{ base: '100%', md: rightWidth ? 'auto' : '280px' }}
      minW={0}>
      {left}
    </VStack>
    <Divider display={{ base: 'block', md: 'none' }} borderColor="cream.line" />
    <Divider orientation="vertical" display={{ base: 'none', md: 'block' }} borderColor="cream.line" alignSelf="stretch" />
    <VStack spacing={{ base: 3, md: '14px' }} align="stretch"
      flex={rightWidth ? { md: `0 0 ${rightWidth}` } : '1'}
      w={{ base: '100%', md: rightWidth }}
      minW={0}>
      {right}
    </VStack>
  </Flex>
);

// ── Main component ────────────────────────────────────────────────────────────

const StudentInfoCard = ({
  student,
  scanMode = 'access_log',
  location,
  studentStatus = null,     // { type, reason, notes, starts_at, ends_at, title, authorized_by, responsible_name }
  pedagogicalExitActive = false, // true si ahora está dentro de la ventana starts_at–ends_at de la salida pedagógica
  isEarlyExit = false,
  scheduledEndTime = null,  // "17:00" — shown in early-exit banner
  isOfflineResult = false,
  autoCountdown = null,     // segundos que faltan para que "Auto" confirme esta tarjeta solo (ver StatusBanner)
  onConfirm,
  onConfirmExit,            // ({ reason, guardianPresent, notes }) => void
  onCreatePedagogicalExit,  // ({ title, authorizedBy, responsibleName, endsAt }) => Promise<void>
  onCreateSickExit,         // ({ title, authorizedBy, responsibleName }) => Promise<void>
  onStatusOverride,         // () => void — admin override for blocked students
  onOpenPickupSurvey,       // () => void — abre la ficha de retiro anticipado
  pickupSurveyReady,        // true si ya se completó el borrador de la ficha (aún no enviado al servidor)
  onViewPickupSurvey,       // () => void — preview de solo lectura del borrador/ficha ya guardada
  currentStaffUser,         // usuario autenticado — default de "Quien lo permite" en ambos flujos de salida pedagógica
  onOpenPedagogicalSurvey,  // () => void — abre la ficha de responsable de salida pedagógica
  pedagogicalSurveyReady,   // true si ya hay borrador o ficha real (reusada del grupo) lista
  onViewPedagogicalSurvey,  // () => void — preview de solo lectura del borrador/ficha ya guardada
  onConfirmPedagogicalActive,  // () => void — confirma la salida pedagógica ya programada
  pedagogicalEncargado,           // persona elegida como encargado (acompaña la salida) — controlado desde ScannerShell
  onSelectPedagogicalEncargado,   // (person|null) => void
  pedagogicalAuthorizedBy,        // persona elegida como quien autoriza — controlado desde ScannerShell, default currentStaffUser
  onSelectPedagogicalAuthorizedBy,// (person|null) => void
  onOpenSickSurvey,         // () => void — abre la ficha de responsable de salida por enfermedad
  sickSurveyReady,          // true si ya hay borrador o ficha real lista
  onViewSickSurvey,         // () => void — preview de solo lectura del borrador/ficha ya guardada
  onConfirmSickActive,      // () => void — confirma la salida por enfermedad ya programada
  sickEncargado,            // persona elegida como responsable (acompaña la salida) — controlado desde ScannerShell
  onSelectSickEncargado,    // (person|null) => void
  sickAuthorizedBy,         // persona elegida como quien autoriza — controlado desde ScannerShell, default currentStaffUser
  onSelectSickAuthorizedBy, // (person|null) => void
  onCancel,
  confirming,
  error,
}) => {
  const [selectedReason, setSelectedReason] = useState(null);
  const [pedTitle, setPedTitle] = useState('');
  const [pedResponsibleUser, setPedResponsibleUser] = useState(null);   // encargado (ad-hoc)
  const [pedAuthorizedByUser, setPedAuthorizedByUser] = useState(null); // quien permite (ad-hoc)
  const [pedEndsAt, setPedEndsAt] = useState('');
  const [creatingPedagogical, setCreatingPedagogical] = useState(false);
  const [sickTitle, setSickTitle] = useState(SICK_EXIT_TITLES[1]);
  const [sickResponsibleUser, setSickResponsibleUser] = useState(null);   // encargado (ad-hoc)
  const [sickAuthorizedByUser, setSickAuthorizedByUser] = useState(null); // quien permite (ad-hoc)
  const [creatingSick, setCreatingSick] = useState(false);

  const mode   = MODE_CONFIG[scanMode] ?? MODE_CONFIG.access_log;
  const status = studentStatus ? STATUS_CONFIG[studentStatus.type] : null;
  const rawGroup = student?.meta_data?.find(m => m.key === 'group_name')?.value ?? null;
  const group  = rawGroup ? codeToDisplayLabel(normalizeCourseName(rawGroup)) : null;

  const selectReason = (key) => setSelectedReason(key);

  const handleCreatePedagogical = async () => {
    const authorizedByPerson = pedAuthorizedByUser ?? currentStaffUser;
    if (!pedEndsAt || !pedResponsibleUser || !authorizedByPerson) return;
    setCreatingPedagogical(true);
    try {
      await onCreatePedagogicalExit({
        title: pedTitle.trim() || null,
        authorizedBy: authorizedByPerson.name,
        responsibleName: pedResponsibleUser.name,
        endsAt: pedEndsAt,
        // Objetos completos (no solo el nombre) — ScannerShell los reusa como
        // encargado/quien-permite de la ficha de responsable cuando se
        // re-escanee a este alumno (o a otro del mismo curso) y aparezca la
        // tarjeta "SALIDA PEDAGÓGICA" activa, para no volver a pedirlos.
        encargadoPerson: pedResponsibleUser,
        permitePerson: authorizedByPerson,
      });
    } finally {
      setCreatingPedagogical(false);
    }
  };

  const handleCreateSick = async () => {
    const authorizedByPerson = sickAuthorizedByUser ?? currentStaffUser;
    if (!sickResponsibleUser || !authorizedByPerson) return;
    setCreatingSick(true);
    try {
      await onCreateSickExit({
        title: sickTitle,
        authorizedBy: authorizedByPerson.name,
        responsibleName: sickResponsibleUser.name,
      });
    } finally {
      setCreatingSick(false);
    }
  };

  // "Quién retira" y el motivo detallado ahora viven en la ficha de survey
  // (ver onOpenPickupSurvey/pickupSurveyReady) — exit_notes ya no necesita
  // componer ese texto para el motivo "Retiro".
  const buildExitNotes = () => null;

  // Quién retira solo aplica al motivo "Retiro" — se completa llenando la
  // ficha de survey, no queda "incompleto" por campos sueltos.
  const pickupIncomplete = selectedReason === 'pickup' && !pickupSurveyReady;

  // Translúcida a propósito — deja asomar el degradado cálido de fondo
  // (ver WARM_SHELL_BG en ScannerShell) en vez de tapar la pantalla con un
  // bloque blanco opaco. El blur mantiene el texto legible encima. En
  // desktop la tarjeta se ensancha bastante para distribuir el contenido
  // horizontalmente en vez de apilarlo todo.
  const cardProps = {
    bg: 'rgba(255,255,255,.68)',
    backdropFilter: 'blur(12px) saturate(180%)',
    sx: { WebkitBackdropFilter: 'blur(12px) saturate(180%)' },
    fontFamily: 'jakarta',
    borderRadius: { base: '22px', md: '28px' },
    overflow: 'hidden',
    border: '1px solid',
    borderColor: 'rgba(239,90,42,.16)',
    boxShadow: '0 16px 48px -8px rgba(90,50,20,.28)',
    w: '100%',
    maxW: { base: '400px', md: '760px' },
  };

  // Aviso cuando el escaneo fue offline y no se pudo verificar un estado especial
  // (suspensión / licencia / salida pedagógica) contra el servidor.
  const OfflineCaution = () => (
    <HStack px={3} py={2} bg="butter.bg" borderRadius="10px" spacing={2}>
      <Icon as={FiWifiOff} color="butter.ink" boxSize={4} flexShrink={0} />
      <Text fontSize={{ base: '13px', md: '14px' }} color="butter.ink" fontWeight="600" flex={1}>
        Estado especial sin verificar (sin conexión) — revisa manualmente si corresponde.
      </Text>
    </HStack>
  );

  // ── Error ─────────────────────────────────────────────────────────────────

  if (error) {
    return (
      <Box {...cardProps} maxW={{ base: '400px', md: '480px' }}>
        <StatusBanner bg="#DC2626" icon={FiXCircle} label="QR INVÁLIDO" />
        <VStack p={{ base: 5, md: 6 }} spacing={4}>
          <Text color="ink.700" textAlign="center" fontSize={{ base: '14px', md: '16px' }} fontWeight="600">{error}</Text>
          <Button w="full" variant="outline" {...BTN_GHOST} borderRadius="12px"
            border="1.5px solid" borderColor="cream.line2" color="ink.700"
            _hover={{ borderColor: 'coral.500', color: 'coral.500' }}
            onClick={onCancel}>
            Volver a escanear
          </Button>
        </VStack>
      </Box>
    );
  }

  // ── SUSPENDIDO → entrada bloqueada ────────────────────────────────────────

  if (status?.blocksEntry && scanMode === 'access_log') {
    return (
      <Box {...cardProps}>
        <StatusBanner bg="#DC2626" icon={FiSlash} label="SUSPENDIDO" />
        <SplitBody
          left={<>
            {isOfflineResult && <OfflineCaution />}
            <StudentInfo student={student} group={group} location={location} />
          </>}
          right={<>
            <Box bg="rose.bg" p={DETAIL_PAD} borderRadius="12px">
              <VStack spacing="6px" align="flex-start">
                {studentStatus?.reason && (
                  <HStack spacing={2} align="flex-start">
                    <Text fontSize={DETAIL_TEXT} color="rose.ink" fontWeight="800" flexShrink={0}>Motivo:</Text>
                    <Text fontSize={DETAIL_TEXT} color="rose.ink" fontWeight="500">{studentStatus.reason}</Text>
                  </HStack>
                )}
                {studentStatus?.ends_at && (
                  <HStack spacing={2}>
                    <Icon as={FiCalendar} color="rose.ink" boxSize={3} flexShrink={0} />
                    <Text fontSize={DETAIL_TEXT} color="rose.ink" fontWeight="500">
                      Hasta: {new Date(studentStatus.ends_at).toLocaleDateString('es-CL', { day: 'numeric', month: 'long' })}
                    </Text>
                  </HStack>
                )}
                {studentStatus?.authorized_by && (
                  <HStack spacing={2}>
                    <Text fontSize={DETAIL_TEXT} color="rose.ink" fontWeight="800" flexShrink={0}>Autorizado por:</Text>
                    <Text fontSize={DETAIL_TEXT} color="rose.ink" fontWeight="500">{studentStatus.authorized_by}</Text>
                  </HStack>
                )}
              </VStack>
            </Box>
            <VStack spacing={2}>
              <Button w="full" bg="#DC2626" color="#fff" _hover={{ bg: '#B91C1C' }}
                {...BTN_LG} isDisabled leftIcon={<Icon as={FiSlash} />}>
                Acceso no permitido
              </Button>
              {onStatusOverride && (
                <Button w="full" variant="ghost" h="44px" fontSize="13.5px" color="ink.400" onClick={onStatusOverride}>
                  Anular bloqueo (requiere autorización dirección)
                </Button>
              )}
              <Button w="full" variant="ghost" {...BTN_GHOST} borderRadius="12px" onClick={onCancel} color="ink.400">
                Cancelar
              </Button>
            </VStack>
          </>}
        />
      </Box>
    );
  }

  // ── LICENCIA MÉDICA → entrada con advertencia ─────────────────────────────

  if (studentStatus?.type === 'medical_leave' && scanMode === 'access_log') {
    return (
      <Box {...cardProps}>
        <StatusBanner bg="#D97706" icon={FiActivity} label="LICENCIA MÉDICA" />
        <SplitBody
          left={<>
            {isOfflineResult && <OfflineCaution />}
            <StudentInfo student={student} group={group} location={location} />
          </>}
          right={<>
            <Box bg="butter.bg" p={DETAIL_PAD} borderRadius="12px">
              <VStack spacing="6px" align="flex-start">
                {studentStatus?.reason && (
                  <HStack spacing={2} align="flex-start">
                    <Text fontSize={DETAIL_TEXT} color="butter.ink" fontWeight="800" flexShrink={0}>Motivo:</Text>
                    <Text fontSize={DETAIL_TEXT} color="butter.ink" fontWeight="500">{studentStatus.reason}</Text>
                  </HStack>
                )}
                {studentStatus?.ends_at && (
                  <HStack spacing={2}>
                    <Icon as={FiCalendar} color="butter.ink" boxSize={3} flexShrink={0} />
                    <Text fontSize={DETAIL_TEXT} color="butter.ink" fontWeight="500">
                      Hasta: {new Date(studentStatus.ends_at).toLocaleDateString('es-CL', { day: 'numeric', month: 'long' })}
                    </Text>
                  </HStack>
                )}
              </VStack>
            </Box>
            <VStack spacing={2}>
              <Button
                w="full" bg="#D97706" color="#fff" _hover={{ bg: '#B45309' }}
                {...BTN_LG}
                onClick={onConfirm} isLoading={confirming} loadingText="Registrando…"
                leftIcon={<Icon as={FiLogIn} />}
              >
                Registrar reintegro
              </Button>
              <AutoCountdownNote countdown={autoCountdown} />
              <Button w="full" variant="ghost" {...BTN_GHOST} borderRadius="12px" onClick={onCancel} isDisabled={confirming} color="ink.400">
                Cancelar
              </Button>
            </VStack>
          </>}
        />
      </Box>
    );
  }

  // ── SALIDA PEDAGÓGICA ─────────────────────────────────────────────────────

  if (studentStatus?.type === 'pedagogical_exit' && scanMode === 'exit_log' && pedagogicalExitActive) {
    return (
      <Box {...cardProps}>
        <StatusBanner bg="#6D28D9" icon={FiMap} label="SALIDA PEDAGÓGICA" />
        <SplitBody
          left={<>
            {isOfflineResult && <OfflineCaution />}
            <StudentInfo student={student} group={group} location={location} />
          </>}
          right={<>
            {(studentStatus?.title || studentStatus?.responsible_name || studentStatus?.authorized_by || studentStatus?.ends_at) && (
              <Box bg="lav.bg" p={DETAIL_PAD} borderRadius="12px">
                <VStack spacing="6px" align="flex-start">
                  {studentStatus?.title && (
                    <Text fontSize={{ base: '13px', md: '15px' }} color="lav.ink" fontWeight="700">{studentStatus.title}</Text>
                  )}
                  {/* "Responsable" (encargado, acompaña la salida) y
                      "Autorizado por" (quien la permite) son dos personas
                      distintas — antes esta etiqueta decía "Responsable" pero
                      mostraba el valor de authorized_by. */}
                  {studentStatus?.responsible_name && (
                    <HStack spacing={2}>
                      <Text fontSize={DETAIL_TEXT} color="lav.ink" fontWeight="600" flexShrink={0}>Responsable:</Text>
                      <Text fontSize={DETAIL_TEXT} color="lav.ink" fontWeight="500">{studentStatus.responsible_name}</Text>
                    </HStack>
                  )}
                  {studentStatus?.authorized_by && (
                    <HStack spacing={2}>
                      <Text fontSize={DETAIL_TEXT} color="lav.ink" fontWeight="600" flexShrink={0}>Autorizado por:</Text>
                      <Text fontSize={DETAIL_TEXT} color="lav.ink" fontWeight="500">{studentStatus.authorized_by}</Text>
                    </HStack>
                  )}
                  {studentStatus?.ends_at && (
                    <HStack spacing={2}>
                      <Icon as={FiClock} color="lav.ink" boxSize={3} flexShrink={0} />
                      {/* El backend manda "HH:mm" (sin fecha) para este campo en
                          salidas pedagógicas — no es una fecha completa parseable. */}
                      <Text fontSize={DETAIL_TEXT} color="lav.ink" fontWeight="500">Hasta las {studentStatus.ends_at}</Text>
                    </HStack>
                  )}
                </VStack>
              </Box>
            )}

            {/* Ficha de responsable — captura firma de quien retira/acompaña
                al alumno en esta salida pedagógica ya programada. Encargado y
                quien-permite se eligen ACÁ (buscador contra usuarios reales de
                datofy-api, no texto suelto) porque la ficha termina generando
                un survey_response por cada uno (ver
                ScannerShell::handleConfirmPedagogicalActive) — antes quedaba
                atribuida al alumno escaneado, lo cual estaba mal. */}
            <Box bg="lav.bg" borderRadius="12px" p={DETAIL_PAD}>
              <Text fontSize={{ base: '13px', md: '14px' }} fontWeight="800" color="lav.ink" textTransform="uppercase" letterSpacing=".06em" mb={2}>
                Responsable <Text as="span" color="#DC4D20">*</Text>
              </Text>
              {pedagogicalSurveyReady ? (
                <HStack justify="space-between" bg="mint.bg" borderRadius="10px" p="10px">
                  <Text fontSize={{ base: '13px', md: '14.5px' }} fontWeight="700" color="mint.ink">
                    Ficha de responsable completada ✓
                  </Text>
                  <Button variant="ghost" size="md" fontSize="14.5px" color="mint.ink" onClick={onViewPedagogicalSurvey}>
                    Ver
                  </Button>
                </HStack>
              ) : (
                <VStack spacing={2} align="stretch">
                  <VStack spacing="6px" align="stretch">
                    <Text fontSize={{ base: '12.5px', md: '13.5px' }} fontWeight="700" color="lav.ink">
                      Encargado (acompaña la salida) <Text as="span" color="#DC4D20">*</Text>
                    </Text>
                    <StaffPicker
                      value={pedagogicalEncargado}
                      onSelect={onSelectPedagogicalEncargado}
                      placeholder="Buscar encargado…"
                    />
                  </VStack>
                  <VStack spacing="6px" align="stretch">
                    <Text fontSize={{ base: '12.5px', md: '13.5px' }} fontWeight="700" color="lav.ink">
                      Quien lo permite
                    </Text>
                    <StaffPicker
                      value={pedagogicalAuthorizedBy ?? currentStaffUser}
                      onSelect={onSelectPedagogicalAuthorizedBy}
                      placeholder="Buscar quien autoriza…"
                    />
                  </VStack>
                  <Button
                    w="full" variant="outline" {...BTN_GHOST} borderRadius="10px"
                    border="1.5px solid" borderColor="cream.line2" color="ink.700"
                    _hover={{ borderColor: 'lav.ink', color: 'lav.ink' }}
                    isDisabled={!pedagogicalEncargado}
                    onClick={onOpenPedagogicalSurvey}
                  >
                    Completar ficha de responsable
                  </Button>
                </VStack>
              )}
            </Box>

            <VStack spacing={2}>
              <Button
                w="full" bg="#6D28D9" color="#fff" _hover={{ bg: '#5B21B6' }}
                {...BTN_LG}
                isDisabled={!pedagogicalSurveyReady}
                onClick={onConfirmPedagogicalActive} isLoading={confirming} loadingText="Registrando…"
                leftIcon={<Icon as={FiMap} />}
              >
                Confirmar salida pedagógica
              </Button>
              <AutoCountdownNote countdown={autoCountdown} />
              <Button w="full" variant="ghost" {...BTN_GHOST} borderRadius="12px" onClick={onCancel} isDisabled={confirming} color="ink.400">
                Cancelar
              </Button>
            </VStack>
          </>}
        />
      </Box>
    );
  }

  // ── SALIDA POR ENFERMEDAD (pre-programada) ────────────────────────────────
  // Mismo patrón de ficha+firma que Salida Pedagógica (ver bloque de arriba):
  // el título/responsable/autorizado ya capturados al crear el estado se
  // muestran como referencia, pero confirmar exige completar la ficha de
  // responsable (dos firmas — responsable y autorizado por) antes de poder
  // registrar la salida.

  if (studentStatus?.type === 'sick_exit' && scanMode === 'exit_log') {
    return (
      <Box {...cardProps}>
        <StatusBanner bg="#0F766E" icon={FiActivity} label="SALIDA POR ENFERMEDAD" />
        <SplitBody
          left={<>
            {isOfflineResult && <OfflineCaution />}
            <StudentInfo student={student} group={group} location={location} />
          </>}
          right={<>
            {(studentStatus?.title || studentStatus?.responsible_name || studentStatus?.authorized_by) && (
              <Box bg="mint.bg" p={DETAIL_PAD} borderRadius="12px">
                <VStack spacing="6px" align="flex-start">
                  {studentStatus?.title && (
                    <Text fontSize={{ base: '13px', md: '15px' }} color="mint.ink" fontWeight="700">{studentStatus.title}</Text>
                  )}
                  {studentStatus?.responsible_name && (
                    <HStack spacing={2}>
                      <Text fontSize={DETAIL_TEXT} color="mint.ink" fontWeight="600" flexShrink={0}>Responsable:</Text>
                      <Text fontSize={DETAIL_TEXT} color="mint.ink" fontWeight="500">{studentStatus.responsible_name}</Text>
                    </HStack>
                  )}
                  {studentStatus?.authorized_by && (
                    <HStack spacing={2}>
                      <Text fontSize={DETAIL_TEXT} color="mint.ink" fontWeight="600" flexShrink={0}>Autorizado por:</Text>
                      <Text fontSize={DETAIL_TEXT} color="mint.ink" fontWeight="500">{studentStatus.authorized_by}</Text>
                    </HStack>
                  )}
                </VStack>
              </Box>
            )}

            {/* Ficha de responsable — misma lógica que Salida Pedagógica,
                dos firmas (responsable y autorizado por), ver
                ScannerShell::handleConfirmSickActive. */}
            <Box bg="mint.bg" borderRadius="12px" p={DETAIL_PAD}>
              <Text fontSize={{ base: '13px', md: '14px' }} fontWeight="800" color="mint.ink" textTransform="uppercase" letterSpacing=".06em" mb={2}>
                Responsable <Text as="span" color="#DC4D20">*</Text>
              </Text>
              {sickSurveyReady ? (
                <HStack justify="space-between" bg="white" borderRadius="10px" p="10px">
                  <Text fontSize={{ base: '13px', md: '14.5px' }} fontWeight="700" color="mint.ink">
                    Ficha de responsable completada ✓
                  </Text>
                  <Button variant="ghost" size="md" fontSize="14.5px" color="mint.ink" onClick={onViewSickSurvey}>
                    Ver
                  </Button>
                </HStack>
              ) : (
                <VStack spacing={2} align="stretch">
                  <VStack spacing="6px" align="stretch">
                    <Text fontSize={{ base: '12.5px', md: '13.5px' }} fontWeight="700" color="mint.ink">
                      Responsable (acompaña la salida) <Text as="span" color="#DC4D20">*</Text>
                    </Text>
                    <GuardianPicker
                      metaData={student?.meta_data}
                      value={sickEncargado}
                      onSelect={onSelectSickEncargado}
                      variant="dropdown"
                    />
                  </VStack>
                  <VStack spacing="6px" align="stretch">
                    <Text fontSize={{ base: '12.5px', md: '13.5px' }} fontWeight="700" color="mint.ink">
                      Quien lo permite
                    </Text>
                    <StaffPicker
                      value={sickAuthorizedBy ?? currentStaffUser}
                      onSelect={onSelectSickAuthorizedBy}
                      placeholder="Buscar quien autoriza…"
                    />
                  </VStack>
                  <Button
                    w="full" variant="outline" {...BTN_GHOST} borderRadius="10px"
                    border="1.5px solid" borderColor="cream.line2" color="ink.700"
                    _hover={{ borderColor: 'mint.ink', color: 'mint.ink' }}
                    isDisabled={!sickEncargado}
                    onClick={onOpenSickSurvey}
                  >
                    Completar ficha de responsable
                  </Button>
                </VStack>
              )}
            </Box>

            <VStack spacing={2}>
              <Button
                w="full" bg="#0F766E" color="#fff" _hover={{ bg: '#0D5F58' }}
                {...BTN_LG}
                isDisabled={!sickSurveyReady}
                onClick={onConfirmSickActive}
                isLoading={confirming} loadingText="Registrando…"
                leftIcon={<Icon as={FiActivity} />}
              >
                Confirmar salida por enfermedad
              </Button>
              <AutoCountdownNote countdown={autoCountdown} />
              <Button w="full" variant="ghost" {...BTN_GHOST} borderRadius="12px" onClick={onCancel} isDisabled={confirming} color="ink.400">
                Cancelar
              </Button>
            </VStack>
          </>}
        />
      </Box>
    );
  }

  // ── SUSPENDIDO → salida permitida con advertencia ─────────────────────────

  if (studentStatus?.type === 'suspended' && scanMode === 'exit_log') {
    return (
      <Box {...cardProps}>
        <StatusBanner bg="#EF4444" icon={FiSlash} label="SUSPENDIDO · SALIDA" />
        <SplitBody
          left={<>
            {isOfflineResult && <OfflineCaution />}
            <StudentInfo student={student} group={group} location={location} />
          </>}
          right={<>
            <HStack px={DETAIL_PAD} py={DETAIL_PAD} bg="rose.bg" borderRadius="10px" spacing={2}>
              <Icon as={FiAlertTriangle} color="rose.ink" boxSize={4} flexShrink={0} />
              <Text fontSize={DETAIL_TEXT} color="rose.ink" fontWeight="600">
                El alumno está suspendido. Se registrará la salida con esta anotación.
              </Text>
            </HStack>
            <VStack spacing={2}>
              <Button
                w="full" variant="outline" border="1.5px solid" borderColor="#EF4444" color="#EF4444"
                _hover={{ bg: 'rose.bg' }}
                {...BTN_LG}
                onClick={onConfirm} isLoading={confirming} loadingText="Registrando…"
                leftIcon={<Icon as={FiLogOut} />}
              >
                Registrar salida
              </Button>
              <AutoCountdownNote countdown={autoCountdown} />
              <Button w="full" variant="ghost" {...BTN_GHOST} borderRadius="12px" onClick={onCancel} isDisabled={confirming} color="ink.400">
                Cancelar
              </Button>
            </VStack>
          </>}
        />
      </Box>
    );
  }

  // ── SALIDA ANTICIPADA → requiere motivo ───────────────────────────────────

  if (isEarlyExit && scanMode === 'exit_log') {
    const canConfirm = selectedReason !== null && !pickupIncomplete;

    return (
      <Box {...cardProps}>
        <StatusBanner
          bg="#DC4D20"
          icon={FiAlertCircle}
          label="SALIDA ANTICIPADA"
          subtitle={scheduledEndTime ? `Horario fin: ${scheduledEndTime}` : undefined}
        />
        <SplitBody
          left={<>
            {isOfflineResult && <OfflineCaution />}
            <StudentInfo student={student} group={group} location={location} />

            {/* Motivo de salida — vive junto al perfil como parte del
                "quién y por qué" (contexto), separado de los inputs de
                acción de la derecha (que dependen del motivo elegido). */}
            <VStack spacing={2} align="stretch">
              <Text fontSize={{ base: '13px', md: '14px' }} fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing=".06em">
                Motivo de salida
              </Text>
              <SimpleGrid columns={2} spacing={2}>
                {EXIT_REASONS.map(r => {
                  const isSel = selectedReason === r.key;
                  return (
                    <Box
                      key={r.key}
                      as="button"
                      type="button"
                      p={{ base: 3, md: '14px' }}
                      minH={{ base: '84px', md: '92px' }}
                      bg={isSel ? 'coral.tint' : 'cream.bg2'}
                      border="2px solid"
                      borderColor={isSel ? 'coral.500' : 'transparent'}
                      borderRadius="12px"
                      cursor="pointer"
                      onClick={() => selectReason(r.key)}
                      transition="all 0.15s"
                      _hover={{ borderColor: 'coral.300', bg: 'coral.tint' }}
                      textAlign="center"
                    >
                      <VStack spacing="6px">
                        <Icon as={r.icon} boxSize={{ base: 7, md: 8 }} color={isSel ? 'coral.600' : 'ink.500'} />
                        <Text fontSize={{ base: '14px', md: '15.5px' }} fontWeight={isSel ? '800' : '700'} color={isSel ? 'coral.700' : 'ink.700'} lineHeight="1.2">
                          {r.label}
                        </Text>
                      </VStack>
                    </Box>
                  );
                })}
              </SimpleGrid>
            </VStack>
          </>}
          right={<>
            {!selectedReason && (
              <HStack px={DETAIL_PAD} py={DETAIL_PAD} bg="cream.bg2" borderRadius="10px" spacing={2}>
                <Icon as={FiAlertCircle} color="ink.400" boxSize={4} flexShrink={0} />
                <Text fontSize={DETAIL_TEXT} color="ink.400" fontWeight="600">
                  Selecciona un motivo para continuar.
                </Text>
              </HStack>
            )}

            {/* Quién retira — solo aplica al motivo "Retiro". Ahora se completa
                vía la ficha de survey "Retiro Anticipado" (datofy-api), no con
                inputs sueltos — queda estructurado y con firma incluida. */}
            {selectedReason === 'pickup' && (
              <Box bg="cream.bg2" borderRadius="12px" p={DETAIL_PAD}>
                <Text fontSize={{ base: '13px', md: '14px' }} fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing=".06em" mb={2}>
                  Quién retira <Text as="span" color="#DC4D20">*</Text>
                </Text>
                {pickupSurveyReady ? (
                  <HStack justify="space-between" bg="mint.bg" borderRadius="10px" p="10px">
                    <Text fontSize={{ base: '13px', md: '14.5px' }} fontWeight="700" color="mint.ink">
                      Ficha de retiro completada ✓
                    </Text>
                    <Button variant="ghost" size="md" fontSize="14.5px" color="mint.ink" onClick={onViewPickupSurvey}>
                      Ver
                    </Button>
                  </HStack>
                ) : (
                  <Button
                    w="full" variant="outline" {...BTN_GHOST} borderRadius="10px"
                    border="1.5px solid" borderColor="cream.line2" color="ink.700"
                    _hover={{ borderColor: 'coral.500', color: 'coral.500' }}
                    onClick={onOpenPickupSurvey}
                  >
                    Completar ficha de retiro
                  </Button>
                )}
              </Box>
            )}

            {/* Salida pedagógica no programada — se crea al vuelo para TODO el
                curso del alumno (misma tabla que Admin > Salidas Pedagógicas,
                no un registro individual); no es un retiro, no pide "Quién
                retira". */}
            {selectedReason === 'pedagogical' && (
              <Box bg="lav.bg" borderRadius="12px" p={DETAIL_PAD}>
                <Text fontSize={{ base: '13px', md: '14px' }} fontWeight="800" color="lav.ink" textTransform="uppercase" letterSpacing=".06em" mb={2}>
                  No estaba programada — autorizar a todo el curso
                </Text>
                {group && (
                  <Text fontSize={{ base: '12px', md: '13.5px' }} color="lav.ink" fontWeight="600" mb={2}>
                    Se registrará para: {group}
                  </Text>
                )}
                <VStack spacing={2} align="stretch">
                  <Input
                    size="md" borderRadius="9px" border="1.5px solid" borderColor="cream.line2"
                    placeholder="Título o motivo (ej: Salida a museo)"
                    value={pedTitle}
                    onChange={e => setPedTitle(e.target.value)}
                    _focus={{ borderColor: 'coral.500', boxShadow: 'none' }}
                  />
                  <VStack spacing="6px" align="stretch">
                    <Text fontSize={{ base: '12.5px', md: '13.5px' }} fontWeight="700" color="lav.ink">
                      Encargado (acompaña la salida) <Text as="span" color="#DC4D20">*</Text>
                    </Text>
                    <StaffPicker
                      value={pedResponsibleUser}
                      onSelect={setPedResponsibleUser}
                      placeholder="Buscar encargado…"
                    />
                  </VStack>
                  <VStack spacing="6px" align="stretch">
                    <Text fontSize={{ base: '12.5px', md: '13.5px' }} fontWeight="700" color="lav.ink">
                      Quien lo permite
                    </Text>
                    <StaffPicker
                      value={pedAuthorizedByUser ?? currentStaffUser}
                      onSelect={setPedAuthorizedByUser}
                      placeholder="Buscar quien autoriza…"
                    />
                  </VStack>
                  <HStack spacing={2} align="center">
                    <Text fontSize={{ base: '12px', md: '13.5px' }} color="ink.700" fontWeight="600" flexShrink={0}>Hasta las</Text>
                    <Input
                      type="time" size="md" borderRadius="9px" border="1.5px solid" borderColor="cream.line2"
                      value={pedEndsAt}
                      onChange={e => setPedEndsAt(e.target.value)}
                      _focus={{ borderColor: 'coral.500', boxShadow: 'none' }}
                    />
                  </HStack>
                </VStack>
              </Box>
            )}

            {/* Salida por enfermedad no programada — a diferencia de Salida
                Pedagógica, es individual (no autoriza a todo el curso) y
                solo pide título/responsable/autorizado — la fecha es
                implícitamente hoy, no pide hora de término. */}
            {selectedReason === 'sick' && (
              <Box bg="mint.bg" borderRadius="12px" p={DETAIL_PAD}>
                <Text fontSize={{ base: '13px', md: '14px' }} fontWeight="800" color="mint.ink" textTransform="uppercase" letterSpacing=".06em" mb={2}>
                  No estaba programada
                </Text>
                <VStack spacing={2} align="stretch">
                  <VStack spacing="6px" align="stretch">
                    <Text fontSize={{ base: '12.5px', md: '13.5px' }} fontWeight="700" color="mint.ink">
                      Título
                    </Text>
                    <Select
                      value={sickTitle}
                      onChange={e => setSickTitle(e.target.value)}
                      size="md" h="48px" fontSize="15px" borderRadius="9px"
                      border="1.5px solid" borderColor="cream.line2" bg="white"
                      _focus={{ borderColor: 'coral.500', boxShadow: 'none' }}
                    >
                      {SICK_EXIT_TITLES.map(t => <option key={t} value={t}>{t}</option>)}
                    </Select>
                  </VStack>
                  <VStack spacing="6px" align="stretch">
                    <Text fontSize={{ base: '12.5px', md: '13.5px' }} fontWeight="700" color="mint.ink">
                      Responsable (acompaña la salida) <Text as="span" color="#DC4D20">*</Text>
                    </Text>
                    <GuardianPicker
                      metaData={student?.meta_data}
                      value={sickResponsibleUser}
                      onSelect={setSickResponsibleUser}
                      variant="dropdown"
                    />
                  </VStack>
                  <VStack spacing="6px" align="stretch">
                    <Text fontSize={{ base: '12.5px', md: '13.5px' }} fontWeight="700" color="mint.ink">
                      Quien lo permite
                    </Text>
                    <StaffPicker
                      value={sickAuthorizedByUser ?? currentStaffUser}
                      onSelect={setSickAuthorizedByUser}
                      placeholder="Buscar quien autoriza…"
                    />
                  </VStack>
                </VStack>
              </Box>
            )}

            {/* Buttons */}
            <VStack spacing={2} pt={1}>
              {selectedReason === 'pedagogical' ? (
                <Button
                  w="full" bg="#6D28D9" color="#fff" _hover={{ bg: '#5B21B6' }}
                  {...BTN_LG}
                  isDisabled={!pedEndsAt || !pedResponsibleUser}
                  onClick={handleCreatePedagogical}
                  isLoading={creatingPedagogical}
                  loadingText="Registrando…"
                  leftIcon={creatingPedagogical ? undefined : <Icon as={FiMap} />}
                >
                  Registrar salida pedagógica
                </Button>
              ) : selectedReason === 'sick' ? (
                <Button
                  w="full" bg="#0F766E" color="#fff" _hover={{ bg: '#0D5F58' }}
                  {...BTN_LG}
                  isDisabled={!sickResponsibleUser}
                  onClick={handleCreateSick}
                  isLoading={creatingSick}
                  loadingText="Registrando…"
                  leftIcon={creatingSick ? undefined : <Icon as={FiActivity} />}
                >
                  Registrar salida por enfermedad
                </Button>
              ) : (
                <Button
                  w="full" bg="#DC4D20" color="#fff" _hover={{ bg: '#C0411A' }}
                  {...BTN_LG}
                  isDisabled={!canConfirm}
                  onClick={() => onConfirmExit({ reason: selectedReason, guardianPresent: selectedReason === 'pickup', notes: buildExitNotes() })}
                  isLoading={confirming}
                  loadingText="Registrando…"
                  leftIcon={confirming ? undefined : <Icon as={FiLogOut} />}
                >
                  Confirmar salida anticipada
                </Button>
              )}
              <Button w="full" variant="ghost" {...BTN_GHOST} borderRadius="12px" onClick={onCancel} isDisabled={confirming || creatingPedagogical || creatingSick} color="ink.400">
                Cancelar
              </Button>
            </VStack>
          </>}
        />
      </Box>
    );
  }

  // ── CONSULTA → tab Antecedentes completo, no un resumen aparte ────────────
  // A diferencia de Entrada/Salida/Validación (que solo necesitan confirmar
  // o cancelar), Consulta no tiene acción — su propósito es mostrar todo lo
  // que se sepa del alumno de un vistazo. En vez de un resumen propio y
  // recortado, trae EXACTAMENTE el mismo tab "Antecedentes" que ya existe en
  // el Perfil del alumno (ver ConsultaResult.js), con una cabecera de solo
  // lectura (avatar/nombre/meta/activo/curso) arriba — sin los 3 stats ni
  // los botones de acción del header de Perfil, que no aplican acá. Usa el
  // ancho completo de la tarjeta (y uno bastante mayor que el resto de los
  // modos) porque Antecedentes es una grilla de 2 columnas.
  if (scanMode === 'info_only') {
    return (
      <Box {...cardProps} maxW={{ base: '400px', md: '920px' }}>
        <StatusBanner bg={mode.solid} icon={mode.icon} label={mode.label} />
        {isOfflineResult && (
          <Box px={{ base: 4, md: 6 }} pt={3}>
            <OfflineCaution />
          </Box>
        )}
        <ConsultaResult student={student} />
        <Box px={{ base: 4, md: 6 }} pb={{ base: 4, md: 6 }} pt={3}>
          <Button w="full" variant="ghost" {...BTN_GHOST} borderRadius="12px" onClick={onCancel} color="ink.400">
            Cerrar
          </Button>
        </Box>
      </Box>
    );
  }

  // ── FLUJO NORMAL (entrada / salida normal / validación) ───────────────────
  // Columna derecha angosta (30%) a propósito — acá solo hay 1-2 botones,
  // dársela toda con flex:1 se veía desproporcionado frente a la columna de
  // identidad. La izquierda pasa a fluida para aprovechar ese espacio.

  return (
    <Box {...cardProps}>
      <StatusBanner bg={mode.solid} icon={mode.icon} label={mode.label} />

      <SplitBody
        rightWidth="30%"
        left={<>
          {isOfflineResult && !status && <OfflineCaution />}

          {/* Non-blocking status banner — se omite si es salida pedagógica fuera de su
              ventana horaria (ya no aplica el mensaje "autorizada") */}
          {status && (scanMode !== 'exit_log' || studentStatus.type !== 'pedagogical_exit' || pedagogicalExitActive) && (
            <HStack px={3} py={2} bg={status.pastelBg} borderRadius="10px" spacing={2}>
              <Icon as={status.icon} color={status.pastelInk} boxSize={4} flexShrink={0} />
              <Text fontSize={{ base: '13px', md: '14px' }} color={status.pastelInk} fontWeight="600" flex={1}>
                {scanMode === 'access_log' ? status.entryMessage : status.exitMessage}
              </Text>
            </HStack>
          )}

          <StudentInfo student={student} group={group} location={location} />
        </>}
        right={<>
          {mode.confirmLabel && (
            <Button
              w="full" bg={mode.solid} color="#fff" _hover={{ filter: 'brightness(0.92)' }}
              {...BTN_LG}
              onClick={onConfirm} isLoading={confirming} loadingText="Registrando…"
              leftIcon={confirming ? undefined : <Icon as={mode.icon} />}
            >
              {mode.confirmLabel}
            </Button>
          )}
          <AutoCountdownNote countdown={autoCountdown} />
          <Button
            w="full" variant="ghost" {...BTN_GHOST} borderRadius="12px" onClick={onCancel}
            isDisabled={confirming} color="ink.400"
          >
            {mode.confirmLabel ? 'Cancelar' : 'Cerrar'}
          </Button>
        </>}
      />
    </Box>
  );
};

export default StudentInfoCard;
